import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { AuthConfig, JwksValidationHandler, OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import moment from 'moment';
import { StellairAuthCode } from 'src/app/pages/stellair/callback-page/callback-page.component';
import { InfosPatient } from 'src/app/utils/interfaces/new/infos-patient.interface';
import { environment } from 'src/environments/environment';
import { StellairApiCallInfos, StellairApiInfosMap, StellairApiInfosMapKey, StellairApiService } from './stellair.api.service';
import { Terminal } from './model/terminal';
import { AccessHealthcareToken } from './model/accessHealthcareToken';
import { VitalePatientResponse } from './model/vitalePatientResponse';
import { PatientCreationRequest } from './model/patientCreationRequest';
import { DonneesAdriPatientDto } from './model/donneesAdriPatientDto';
import { BehaviorSubject } from 'rxjs';
import { DonneesAmoDto } from './model/donneesAmoDto';
import { PatientResponse } from './model/patientResponse';

const STELLAIR_AUTH_BASEPATH = `${environment.stellairAuthBasepath}oauth2/authorize`;
const STELLAIR_TOKEN_BASEPATH = `${environment.stellairAuthBasepath}oauth2/token`;
const STELLAIR_CLIENT_ID = environment.stellairAuthClientId;
const STELLAIR_CALLBACK_PATH = environment.stellairAuthCallbackPath;

const HYPERMED_STELLAIR_TOKEN = '/stellair/token';

export const STELLAIR_AUTH_CODE_STORAGE_PREFIX = 'stellair_authorization_code';
export const STELLAIR_VERIFIER_STORAGE_PREFIX = 'PKCE_verifier';

const NEED_AUTH_CODE_ERROR_MESSAGE = 'NEED_AUTH_CODE';

@Injectable({
  providedIn: 'root'
})
export class StellairService {

  private __accessToken: string | undefined = undefined;
  private get _accessToken(): string | undefined {
    return this.__accessToken;
  }
  private set _accessToken(value: string | undefined) {
    this.__accessToken = value;
    this.stellairAvailability();
  }

  private __healthcareToken: AccessHealthcareToken | undefined = undefined;
  private get _healthcareToken(): AccessHealthcareToken | undefined {
    return this.__healthcareToken;
  }
  private set _healthcareToken(value: AccessHealthcareToken | undefined) {
    this.__healthcareToken = value;
    this.stellairAvailability();
  }

  public isStellairAvailable: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private stellairAvailability(): void {
    this.isStellairAvailable.next(!!this.__accessToken && !!this.__healthcareToken);
  }

  constructor(
    private oauthService: OAuthService,
    private _http: HttpClient,
    private _stellairApiSrv: StellairApiService,
  ) { }

  public async getStellairAccessToken(): Promise<string | undefined> {
    try {
      if (!this._accessToken) {
        this._accessToken = await this._getAccessToken();
      }
      return this._accessToken;
    } catch (error) {
      console.error(error);
    }
  }

  public async listTerminaux(): Promise<Array<Terminal>> {
    try {
      return await this._stellairApiSrv.callStellairApi<void, Array<Terminal>>(StellairApiInfosMap.get(StellairApiInfosMapKey.listTerminaux), this._accessToken);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async getHealthcareToken(): Promise<AccessHealthcareToken> {
    try {
      if (!this._healthcareToken || moment().isAfter(moment(this._healthcareToken.expirationTime))) {
        this._healthcareToken = await this._stellairApiSrv.callStellairApi<void, AccessHealthcareToken>(StellairApiInfosMap.get(StellairApiInfosMapKey.healthcareToken), this._accessToken);
      }
      return this._healthcareToken;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async readVital(): Promise<VitalePatientResponse> {
    if (!this.isStellairAvailable.value || !this._accessToken || !this._healthcareToken) {
      throw new Error('Une connexion à Stellair est nécessaire lire la carte Vitale');
    }

    try {
      const apiInfos: StellairApiCallInfos<any> = StellairApiInfosMap.get(StellairApiInfosMapKey.readVital);
      apiInfos.headers = [
        {
          key: 'x-stellair-healthcare-token',
          value: this._healthcareToken.token,
        }
      ];
      return await this._stellairApiSrv.callStellairApi<void, VitalePatientResponse>(apiInfos, this._accessToken);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async createUpdatePatient(patientInfos: PatientCreationRequest): Promise<PatientResponse> {
    if (!this.isStellairAvailable.value || !this._accessToken || !this._healthcareToken) {
      throw new Error('Une connexion à Stellair est nécessaire pour créer ou mettre à jour le patient.');
    }

    try {
      const apiInfos: StellairApiCallInfos<any> = StellairApiInfosMap.get(StellairApiInfosMapKey.createUpdatePatient);
      apiInfos.headers = [
        {
          key: 'x-stellair-healthcare-token',
          value: this._healthcareToken.token,
        },
      ];
      apiInfos.body = patientInfos;
      return await this._stellairApiSrv.callStellairApi<void, PatientResponse>(apiInfos, this._accessToken);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async getFichePatientInfos(stellairPatientId: string): Promise<DonneesAdriPatientDto> {
    if (!this.isStellairAvailable.value || !this._accessToken || !this._healthcareToken) {
      throw new Error('Une connexion à Stellair est nécessaire pour récupérer les informations du patient.');
    }
    try {
      const apiInfos: StellairApiCallInfos<any> = StellairApiInfosMap.get(StellairApiInfosMapKey.getFichePatientInfos);
      apiInfos.headers = [
        {
          key: 'x-stellair-healthcare-token',
          value: this._healthcareToken.token,
        },
      ];
      apiInfos.pathParams = new Map<string, string>([[ 'id', stellairPatientId ]]);
      return await this._stellairApiSrv.callStellairApi<void, DonneesAdriPatientDto>(apiInfos, this._accessToken);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public getPatientStellairUrl(patient: DonneesAmoDto): string | undefined {
    if (patient.prenom && patient.nom && patient.dateNaissance && patient.nirAssure) {
      // const ticket_id = '';
      const sso = 'cognito';

      const querys = [];
      if (patient.prenom) {
        querys.push(`prenom=${patient.prenom}`);
      }
      if (patient.nom) {
        querys.push(`nom=${patient.nom}`);
      }
      if (patient.nirAssure) {
        querys.push(`nir=${patient.nirAssure}`);
      }
      if (patient.dateNaissance) {
        querys.push(`dateNaissance=${moment(patient.dateNaissance, 'YYYY-MM-DD').format('DD/MM/YYYY')}`);
      }

      const url = `${environment.stellairSiteBasepath}fse/?${querys.join('&')}&sso=${sso}`;

      return url;
    } else {
      return undefined;
    }
  }

  private async _logginToStellair(): Promise<string | undefined> {
    const authCodeFlowConfig: AuthConfig = {
      loginUrl: STELLAIR_AUTH_BASEPATH,
      redirectUri: STELLAIR_CALLBACK_PATH,
      clientId: STELLAIR_CLIENT_ID,
      responseType: 'code',
      scope: 'openid',
      showDebugInformation: true,
      tokenEndpoint: STELLAIR_TOKEN_BASEPATH,
      skipIssuerCheck: true,

    };
    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();

    try {
      await this.oauthService.initLoginFlowInPopup();
    } catch (error) {
      if (error instanceof OAuthErrorEvent) {
        console.error(error);
      } else {
        console.error(error);
      }
    }

    // return undefined;

    const authCode = this._getAuthCode();
    if (authCode) {
      return await this._getAccessToken(authCode);
    } else {
      return undefined;
    }
  }

  public logout(): void {
    this._accessToken = undefined;
    this._healthcareToken = undefined;
  }

  public async doSyncPatient(patientId: string): Promise<void> {
    await this.getStellairAccessToken();
  }

  private async _getAccessToken(authCode?: StellairAuthCode): Promise<string | undefined> {
    try {
      let url = `${environment.basePath}${HYPERMED_STELLAIR_TOKEN}`;

      if (authCode) {
        url += `?authorization-code=${authCode.code}&code-verifier=${authCode.verifier}`;
      }

      const httpOptions = {
        withCredentials: true,
        observe: 'response' as 'response'
      };
      const response = await this._http.get<string>(url, httpOptions).toPromise();
      return response.body;
    } catch (error) {
      if (error.error.message === NEED_AUTH_CODE_ERROR_MESSAGE) {
        return await this._logginToStellair();
      } else {
        throw error;
      }
    }
  }

  private _getAuthCode(): StellairAuthCode | undefined {
    const code = localStorage.getItem(STELLAIR_AUTH_CODE_STORAGE_PREFIX);
    if (code) {
      localStorage.removeItem(STELLAIR_AUTH_CODE_STORAGE_PREFIX);
      return JSON.parse(code) as StellairAuthCode;
    } else {
      return undefined;
    }
  }
}
